import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { IGetPendingAuditStatus } from '../../interfaces/audits/get-audits.interface';
import { IAddDocument } from '../../interfaces/bo-forms/add-bo-document.interface';
import { IRegenerateDocument } from '../../interfaces/bo-forms/regenerate-bo-document.interface';
import { IConfirmModalConfig } from '../../interfaces/modal-dialog-config.interface';
import { AddToastNotif } from '../../types/toast-notification.type';
import { Acp4Api } from '../acp4-api/acp4-api.service';
import { CheckForAuditsService } from '../audits/check-for-audits/check-for-audits.service';
import { AuthApi } from '../auth-api/auth-api.service';
import { ModalDialogService } from '../modalDialog/modal-dialog.service';
import { ToastNotificationsManagerService } from '../toast-notifications-manager/toast-notifications-manager.service';

@Injectable({
  providedIn: 'root',
})
export class GenerateBoDocumentsService {
  getDocumentsSubject = new Subject<void>();

  successNotification: AddToastNotif = {
    type: 'success',
    content: 'Successfully submitted for generation',
  };

  auditNotification: AddToastNotif = {
    type: 'success',
    content: 'Successfully created Audit(s)',
  };

  constructor(
    private apiSvc: Acp4Api,
    private authApi: AuthApi,
    private modalService: ModalDialogService,
    private toastNotif: ToastNotificationsManagerService,
    private checkAuditsService: CheckForAuditsService,
  ) {}

  /**
   * On click confirm button of generate/regenerate modal - gather all selected data, ready to be passed further
   * This generates / regenerates documents.
   * BO documents regenerate requires supplemental data from the DB, so we have to differentiate
   *  between the request coming from the side panel or bo documents section.
   * In addition, for generate docs we need to check if any documents with this combination of BO,
   *  systemID and documentType ID already exists.
   * @param generationData is the data coming in from either the document generation sidepanel or the bo documents (named forms) section
   */

  handleGenerateClick(generationData: any) {
    // If the call is through the document generation side panel:
    if (generationData[0]?.option === 'Generate Docs') return this.handleData(generationData);
    // This path occurs if the call comes through the BO documents section
    // first checking the audit stats of the record and then fetching supplemental data
    else {
      const confirmConfig: IConfirmModalConfig = {
        title: 'Regenerate Form(s)',
        message:
          'Are you sure you want to continue with this action?' +
          'The new document will replace the stored one and any previous version will be deleted.',
        confirmBtnLabel: 'Confirm',
        cancelBtnLabel: 'Cancel',
      };
      this.modalService.showConfirmModal(confirmConfig)?.subscribe(confirm => {
        if (confirm) this.checkAuditStatus(generationData);
      });
    }
  }

  /**
   * This checks the audit status of the document for the regenerate action; it there is an open audit the action can't be performed
   * @param generationData is the data that initially came from BO documents (named forms) section
   */

  checkAuditStatus(generationData: any) {
    const params: IGetPendingAuditStatus = {
      SystemID: this.authApi.SystemID,
      JSON: {
        DocumentID: generationData.DocumentID,
      },
    };
    /**
     * TODO: toast migration note: Code below is ugly and hard to refactor. Clean up whenever worked on
     */
    this.checkAuditsService.checkPendingAuditForDocuments(params).subscribe({
      next: auditsResponse => {
        // Assign the data as normal if there are no open audits
        if (auditsResponse.results[0].OpenAudits === 0) {
          const params: any = {
            // TODO: This will need to be refactored once we generate multiple docs through multiple selection acion item. This might include a change to the SP
            SystemID: this.authApi.SystemID,
            BeneficialOwnerID: generationData.BeneficialOwnerID,
          };
          const formData = generationData;
          this.apiSvc.get(this.apiSvc.URL_BO_PROFILES_LIST, { params: params }).subscribe({
            next: () => this.handleData(formData),
            error: err => this.apiSvc.defaultErrResHandler(err),
          });
          // If there is an open Audit, show a modal informing the user that there is an audit pending.
        } else {
          const confirmConfig: IConfirmModalConfig = {
            title: 'Audit pending',
            message: 'Action is forbidden because an audit pending was found on this item',
            confirmBtnLabel: 'Confirm',
          };
          this.modalService.showConfirmModal(confirmConfig);
        }
      },
      error: err => this.apiSvc.defaultErrResHandler(err),
    });
  }

  /**
   * This function handles incoming data and checks how to structure it based on whether
   *  its called through the generate docs side panel or the bo documents component (named forms).
   * @param generateData comes from the database
   */

  handleData(generateData: any) {
    if (generateData[0]?.option === 'Generate Docs') {
      // If the call comes through the side panel
      const documentsToGenerate = {
        BeneficialOwnerID: generateData[0].BeneficialOwnerID,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        JSON: generateData.map((BoDocs: any) => ({
          DocumentTypeID: BoDocs.DocumentTypeID,
          DocumentFileName: BoDocs.DocumentTypeName,
        })),
        TaxYear: new Date().getFullYear(), //TODO: Make tax year selectable; not just get current year.
        SystemID: this.authApi.SystemID,
      };
      this.requestDocumentsGeneration(documentsToGenerate);
    } else {
      // If the call comes through the BO Documents section
      const documentToCreateAuditFor = {
        SystemID: this.authApi.SystemID,
        DocumentJSON: JSON.stringify({
          ReferenceID: generateData.DocumentID,
          Note: 'Regenerate Form',
          Changes: [],
        }),
      };
      this.requestDocumentsRegeneration(documentToCreateAuditFor);
    }
  }

  /**
   * requestDocumentsGeneration() handles the api call for the generation process coming from the BO generation sidepanel.
   * @param documentsToGenerate comes from handleData(), its the variables we need for the SP call usp_AddDocument
   */

  requestDocumentsGeneration(documentsToGenerate: IAddDocument) {
    this.apiSvc.post(this.apiSvc.URL_ADD_BO_FORM, documentsToGenerate).subscribe({
      next: () => {
        this.toastNotif.addNew(this.successNotification);
        this.getDocumentsSubject.next();
      },
      error: err => this.apiSvc.defaultErrResHandler(err),
    });
  }

  /**
   * requestDocumentsRegeneration() handles the api call for the regeneration of the bo documents comin in from the BO documents section
   * @param documentToRequestRegenerationFor comes from handleData(), its the variables we need for the SP call usp_RegenerateDocument
   */

  requestDocumentsRegeneration(documentToRequestRegenerationFor: IRegenerateDocument) {
    this.apiSvc
      .put(this.apiSvc.URL_MULTIPLE_REGENERATE_DOCS, documentToRequestRegenerationFor)
      .subscribe({
        next: () => this.toastNotif.addNew(this.successNotification),
        error: err => this.apiSvc.defaultErrResHandler(err),
      });
  }
}
